import React, { useEffect, useState } from "react";
import { WorkOrdersAPI, KIND_LABELS, WorkLine, WorkOrderKind, WorkOrderDto } from "../../api/workOrders";
import { UsersAPI, UserDto } from "../../api/usersApi";
import { getClients, ClientDto } from "../../api/clients";

interface Props {
  dealId?: number;
  /** редактирование существующего наряда */
  order?: WorkOrderDto;
  onClose: () => void;
  onSaved: (o: WorkOrderDto) => void;
}

const emptyLine = (): WorkLine => ({ name: "", qty: 1, unit: "шт" });

function toLocalInput(iso?: string | null) {
  if (!iso) return "";
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export default function CreateWorkOrderModal({ dealId, order, onClose, onSaved }: Props) {
  const [kind, setKind] = useState<WorkOrderKind>(order?.kind ?? "video");
  const [title, setTitle] = useState(order?.title ?? "");
  const [clientId, setClientId] = useState<number | null>(order?.clientId ?? null);
  const [address, setAddress] = useState(order?.address ?? "");
  const [phone, setPhone] = useState(order?.contactPhone ?? "");
  const [description, setDescription] = useState(order?.description ?? "");
  const [assigneeId, setAssigneeId] = useState<number | null>(order?.assigneeId ?? null);
  const [coIds, setCoIds] = useState<number[]>((order?.coAssigneeIds ?? []).map(Number));
  const [scheduledAt, setScheduledAt] = useState(toLocalInput(order?.scheduledAt));
  const [works, setWorks] = useState<WorkLine[]>(order?.works?.length ? order.works : [emptyLine()]);
  const [materials, setMaterials] = useState<WorkLine[]>(order?.materials ?? []);
  const [users, setUsers] = useState<UserDto[]>([]);
  const [clients, setClients] = useState<ClientDto[]>([]);
  const [dealIdState] = useState<number | null>(dealId ?? order?.dealId ?? null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    UsersAPI.getAll().then((u) => setUsers(u.filter((x) => x.isActive))).catch(() => {});
    getClients().then((r) => setClients(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!dealId || order) return;
    WorkOrdersAPI.prefill(dealId)
      .then((p) => {
        setTitle(p.title);
        setClientId(p.clientId);
        setAddress(p.address);
        setPhone(p.contactPhone);
        setDescription(p.description);
        if (p.works.length) setWorks(p.works);
        setMaterials(p.materials);
      })
      .catch(() => {});
  }, [dealId, order]);

  const setLine = (setter: React.Dispatch<React.SetStateAction<WorkLine[]>>, i: number, patch: Partial<WorkLine>) =>
    setter((list) => list.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const save = async () => {
    if (!title.trim()) { setError("Укажите название наряда"); return; }
    setSaving(true);
    setError("");
    const payload = {
      kind,
      title,
      dealId: dealIdState,
      clientId,
      assigneeId,
      coAssigneeIds: coIds.filter((x) => x !== assigneeId),
      address,
      contactPhone: phone,
      description,
      works: works.filter((w) => w.name.trim()),
      materials: materials.filter((w) => w.name.trim()),
      scheduledAt: scheduledAt ? new Date(scheduledAt).toISOString() : null,
    };
    try {
      const saved = order ? await WorkOrdersAPI.update(order.id, payload) : await WorkOrdersAPI.create(payload);
      onSaved(saved);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  };

  const linesEditor = (label: string, list: WorkLine[], setter: React.Dispatch<React.SetStateAction<WorkLine[]>>) => (
    <div style={{ marginTop: 14 }}>
      <div style={lbl}>{label}</div>
      {list.map((l, i) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 64px 64px 30px", gap: 6, marginBottom: 6 }}>
          <input style={inp} placeholder="Наименование" value={l.name} onChange={(e) => setLine(setter, i, { name: e.target.value })} />
          <input style={inp} type="number" min={0} value={l.qty} onChange={(e) => setLine(setter, i, { qty: Number(e.target.value) })} />
          <input style={inp} value={l.unit ?? ""} onChange={(e) => setLine(setter, i, { unit: e.target.value })} />
          <button style={{ ...smallBtn, color: "#f87171" }} onClick={() => setter((x) => x.filter((_, idx) => idx !== i))} title="Убрать">✕</button>
        </div>
      ))}
      <button style={smallBtn} onClick={() => setter((x) => [...x, emptyLine()])}>+ Добавить строку</button>
    </div>
  );

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h2 style={{ margin: 0, fontSize: 19, color: "#f3f4f6" }}>{order ? `Наряд ${order.number}` : "Новый заказ-наряд"}</h2>
          <button style={{ ...smallBtn, border: "none", fontSize: 18 }} onClick={onClose}>✕</button>
        </div>

        <div style={grid2}>
          <div>
            <div style={lbl}>Вид работ</div>
            <select style={inp} value={kind} onChange={(e) => setKind(e.target.value as WorkOrderKind)}>
              {(Object.keys(KIND_LABELS) as WorkOrderKind[]).map((k) => <option key={k} value={k}>{KIND_LABELS[k]}</option>)}
            </select>
          </div>
          <div>
            <div style={lbl}>Исполнитель</div>
            <select style={inp} value={assigneeId ?? ""} onChange={(e) => setAssigneeId(e.target.value ? Number(e.target.value) : null)}>
              <option value="">— не назначен —</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.lastName} {u.firstName}</option>)}
            </select>
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={lbl}>Соисполнители (работают наравне с исполнителем)</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {users.filter((u) => u.id !== assigneeId).map((u) => {
              const on = coIds.includes(u.id);
              return (
                <button key={u.id} type="button" onClick={() => setCoIds((l) => (on ? l.filter((x) => x !== u.id) : [...l, u.id]))}
                  style={{
                    padding: "4px 11px", borderRadius: 14, fontSize: 12, fontWeight: 600, cursor: "pointer",
                    border: `1px solid ${on ? "#6366f1" : "#374151"}`,
                    background: on ? "rgba(99,102,241,0.18)" : "transparent",
                    color: on ? "#c7d2fe" : "#9ca3af",
                  }}>
                  {on ? "✓ " : "+ "}{u.lastName} {u.firstName}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={lbl}>Название / задача *</div>
          <input style={inp} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Монтаж 4 камер на объекте / Диагностика ноутбука" />
        </div>

        <div style={{ ...grid2, marginTop: 12 }}>
          <div>
            <div style={lbl}>Клиент</div>
            <select style={inp} value={clientId ?? ""} onChange={(e) => setClientId(e.target.value ? Number(e.target.value) : null)}>
              <option value="">— не выбран —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.lastName} {c.firstName}{c.phone ? ` · ${c.phone}` : ""}</option>)}
            </select>
          </div>
          <div>
            <div style={lbl}>Телефон для связи</div>
            <input style={inp} value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
        </div>

        <div style={{ ...grid2, marginTop: 12 }}>
          <div>
            <div style={lbl}>Адрес объекта</div>
            <input style={inp} value={address} onChange={(e) => setAddress(e.target.value)} />
          </div>
          <div>
            <div style={lbl}>Когда выполнить</div>
            <input style={inp} type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={lbl}>Описание / неисправность</div>
          <textarea style={{ ...inp, minHeight: 64, resize: "vertical" }} value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>

        {linesEditor("Перечень работ (наименование · кол-во · ед.)", works, setWorks)}
        {linesEditor("Оборудование и материалы", materials, setMaterials)}

        {error && <div style={{ color: "#f87171", fontSize: 13, marginTop: 12 }}>{error}</div>}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
          <button style={{ ...btn, background: "#374151" }} onClick={onClose}>Отмена</button>
          <button style={{ ...btn, background: "#2563eb" }} onClick={save} disabled={saving}>{saving ? "Сохраняем…" : order ? "Сохранить" : "Создать наряд"}</button>
        </div>
      </div>
    </div>
  );
}

const overlay: React.CSSProperties = { position: "fixed", inset: 0, zIndex: 10000, background: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center", justifyContent: "center", padding: 12 };
const modal: React.CSSProperties = { width: "100%", maxWidth: 640, maxHeight: "92vh", overflowY: "auto", background: "#131926", border: "1px solid #1f2937", borderRadius: 14, padding: 22, boxShadow: "0 20px 60px rgba(0,0,0,0.5)" };
const grid2: React.CSSProperties = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 };
const lbl: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: "#9ca3af", marginBottom: 5 };
const inp: React.CSSProperties = { width: "100%", boxSizing: "border-box", padding: "9px 11px", fontSize: 14, color: "#fff", background: "#1a2030", border: "1px solid #374151", borderRadius: 8, outline: "none", fontFamily: "inherit" };
const btn: React.CSSProperties = { color: "#fff", border: "none", borderRadius: 8, padding: "10px 18px", fontSize: 14, fontWeight: 600, cursor: "pointer" };
const smallBtn: React.CSSProperties = { background: "transparent", color: "#9ca3af", border: "1px solid #374151", borderRadius: 8, padding: "6px 10px", fontSize: 13, cursor: "pointer" };
